/**
 * ValuesRowAddPopover — `[+ add row]` form popover for the INSERT VALUES grid.
 * Surfaces one input per column (matching the existing column header layout).
 * Submitting commits an `add-insert-values-row` op via `addInsertValuesRow`.
 *
 * Without `editContext` the popover renders as a disabled `[+ add row]` button
 * (preserves the VQE-RO scaffolding contract).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.
 */

import { useId, useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '../../../../../components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../components/ui/popover';
import { VqeTooltip } from '../common/VqeTooltip';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addInsertValuesRow } from '../../edit/edits/addInsertValuesRow';
import { vqeCopy } from '../../ux/vqeCopy';

export interface ValuesRowAddPopoverProps {
  readonly editContext?: VqeEditContext;
  /** Header labels for each input. Drives column count + placeholder text. */
  readonly headerLabels: readonly string[];
}

export function ValuesRowAddPopover({ editContext, headerLabels }: ValuesRowAddPopoverProps) {
  if (!editContext || headerLabels.length === 0) {
    return (
      <VqeTooltip content={vqeCopy.disabled.editingUnavailable}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          disabled
          aria-disabled
          data-testid="vqe-insert-add-row"
        >
          <Plus className="h-3.5 w-3.5" />
          {vqeCopy.actions.addRow}
        </Button>
      </VqeTooltip>
    );
  }
  return <ValuesRowAddPopoverEnabled editContext={editContext} headerLabels={headerLabels} />;
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly headerLabels: readonly string[];
}

function ValuesRowAddPopoverEnabled({ editContext, headerLabels }: EnabledProps) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<string[]>(() => headerLabels.map(() => ''));
  const baseId = useId();

  const reset = (): void => {
    setValues(headerLabels.map(() => ''));
  };

  const allFilled = values.every((v) => v.trim().length > 0);

  const submit = async (): Promise<void> => {
    if (!allFilled) return;
    try {
      await addInsertValuesRow(values, toCommitCtx(editContext));
      reset();
      setOpen(false);
    } catch {
      // Validation errors keep the popover open so the user can fix the form.
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1 px-2 text-xs text-muted-foreground"
          data-testid="vqe-insert-add-row"
          aria-label="Add VALUES row"
        >
          <Plus className="h-3.5 w-3.5" />
          {vqeCopy.actions.addRow}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 space-y-2"
        data-testid="vqe-insert-add-row-popover"
        // Don't auto-dismiss on a focus shift — a late schema-refresh re-render
        // over the Monaco editor can otherwise tear this open form down
        // mid-edit. Mirrors FilterBuilder / CompactVqePopover; outside
        // pointer-down still dismisses.
        onFocusOutside={(e) => e.preventDefault()}
      >
        {headerLabels.map((label, idx) => {
          const inputId = `${baseId}-${idx}`;
          return (
            <div key={`${label}-${idx}`} className="flex flex-col gap-1">
              <label htmlFor={inputId} className="text-xs font-medium">
                {label}
              </label>
              <input
                id={inputId}
                type="text"
                value={values[idx] ?? ''}
                onChange={(e) => {
                  const copy = [...values];
                  copy[idx] = e.target.value;
                  setValues(copy);
                }}
                data-testid={`vqe-insert-add-row-input-${idx}`}
                className="rounded border border-input bg-background px-2 py-1 font-mono text-xs"
                placeholder="value or expression"
                autoFocus={idx === 0}
              />
            </div>
          );
        })}
        <div className="flex justify-end gap-2 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => {
              reset();
              setOpen(false);
            }}
            data-testid="vqe-insert-add-row-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs"
            disabled={!allFilled}
            onClick={() => {
              void submit();
            }}
            data-testid="vqe-insert-add-row-submit"
          >
            Add row
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
